import {
  Box,
  Card,
  Collapsible,
  Grid,
  HStack,
  Icon,
  Separator,
  Stat,
  Table,
  Text,
} from "@chakra-ui/react";
import { Product } from "@/shared/core-products";
import { Fragment, useState } from "react";
import {
  FiChevronDown,
  FiChevronRight,
  FiDollarSign,
  FiPackage,
} from "react-icons/fi";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@/ui/chakra/dialog";

interface ModalProductsTableProps {
  products: Product[];
  title: string;
  isOpen: boolean;
  onClose: () => void;
}

const formatCLP = (value: number) =>
  new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
  }).format(value);

const headerProps = {
  fontSize: "xs",
  fontWeight: "semibold",
  letterSpacing: "wider",
  textTransform: "uppercase",
  color: "fg.muted",
  whiteSpace: "nowrap",
} as const;

const ModalProductRow = ({ product }: { product: Product }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const totalValue =
    product.externalProduct?.consumedDetails?.reduce(
      (acc, detail) => acc + detail.price * detail.stock,
      0,
    ) || 0;

  return (
    <Fragment key={product.id}>
      <Table.Row
        onClick={() => setIsExpanded(!isExpanded)}
        cursor="pointer"
        _hover={{ bg: "bg.subtle" }}
      >
        <Table.Cell color="fg.muted">
          {isExpanded ? <FiChevronDown /> : <FiChevronRight />}
        </Table.Cell>
        <Table.Cell fontWeight="semibold">
          {product.externalProduct?.code || product.erpProduct?.erpCode}
        </Table.Cell>
        <Table.Cell>{product.description}</Table.Cell>
        <Table.Cell textAlign="end">
          {product.voucherProduct?.amount}
        </Table.Cell>
        <Table.Cell>{product.storage?.name}</Table.Cell>
        <Table.Cell>{product.unit}</Table.Cell>
      </Table.Row>
      <Table.Row>
        <Table.Cell colSpan={6} p={0} bg="bg.subtle" borderBottomWidth={0}>
          <Collapsible.Root open={isExpanded}>
            <Collapsible.Content>
              <Card.Root m={4} variant="outline">
                <Card.Body>
                  <Grid
                    templateColumns={{
                      base: "1fr",
                      md: "repeat(3, 1fr)",
                    }}
                    gap={6}
                    mb={6}
                  >
                    <Stat.Root>
                      <HStack gap={2} mb={1}>
                        <Icon as={FiPackage} color="fg.muted" />
                        <Stat.Label>Cantidad total</Stat.Label>
                      </HStack>
                      <Stat.ValueText>
                        {product.voucherProduct?.amount}
                      </Stat.ValueText>
                      <Stat.HelpText>Total de unidades</Stat.HelpText>
                    </Stat.Root>
                    <Stat.Root>
                      <HStack gap={2} mb={1}>
                        <Icon as={FiDollarSign} color="fg.muted" />
                        <Stat.Label>Valor total</Stat.Label>
                      </HStack>
                      <Stat.ValueText>{formatCLP(totalValue)}</Stat.ValueText>
                    </Stat.Root>
                  </Grid>
                  <Separator mb={6} />
                  <Text fontSize="md" fontWeight="semibold" mb={2}>
                    Detalles de stock
                  </Text>
                  <Table.Root size="sm">
                    <Table.Header>
                      <Table.Row>
                        <Table.ColumnHeader {...headerProps}>
                          #
                        </Table.ColumnHeader>
                        <Table.ColumnHeader {...headerProps}>
                          Cantidad
                        </Table.ColumnHeader>
                        <Table.ColumnHeader {...headerProps}>
                          Precio
                        </Table.ColumnHeader>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {product.externalProduct?.consumedDetails?.map(
                        (detail, index) => (
                          <Table.Row key={index}>
                            <Table.Cell>#{index + 1}</Table.Cell>
                            <Table.Cell>{detail.stock}</Table.Cell>
                            <Table.Cell>{formatCLP(detail.price)}</Table.Cell>
                          </Table.Row>
                        ),
                      )}
                      {!product.externalProduct?.consumedDetails?.length && (
                        <Table.Row>
                          <Table.Cell
                            colSpan={3}
                            textAlign="center"
                            color="fg.muted"
                          >
                            Sin detalle de consumo
                          </Table.Cell>
                        </Table.Row>
                      )}
                    </Table.Body>
                  </Table.Root>
                </Card.Body>
              </Card.Root>
            </Collapsible.Content>
          </Collapsible.Root>
        </Table.Cell>
      </Table.Row>
    </Fragment>
  );
};

const ModalProductsTable: React.FC<ModalProductsTableProps> = ({
  products,
  title,
  isOpen,
  onClose,
}) => {
  return (
    <DialogRoot isOpen={isOpen} onClose={onClose} size="xl" isCentered>
      <DialogContent maxW="6xl">
        <DialogCloseTrigger />
        <DialogHeader borderBottomWidth="1px" borderColor="border">
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <DialogBody pt={4}>
          <Box borderWidth="1px" borderColor="border" rounded="lg">
            <Table.ScrollArea>
              <Table.Root size="sm" variant="line">
                <Table.Header bg="bg.subtle">
                  <Table.Row>
                    <Table.ColumnHeader width="40px" />
                    <Table.ColumnHeader {...headerProps}>
                      Código Microsiga
                    </Table.ColumnHeader>
                    <Table.ColumnHeader {...headerProps}>
                      Descripción
                    </Table.ColumnHeader>
                    <Table.ColumnHeader {...headerProps} textAlign="end">
                      Cantidad
                    </Table.ColumnHeader>
                    <Table.ColumnHeader {...headerProps}>
                      Bodega
                    </Table.ColumnHeader>
                    <Table.ColumnHeader {...headerProps}>
                      Unidad
                    </Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {products.map((product, index) => (
                    <ModalProductRow product={product} key={index} />
                  ))}
                  {products.length === 0 && (
                    <Table.Row>
                      <Table.Cell
                        colSpan={6}
                        textAlign="center"
                        py={8}
                        color="fg.muted"
                      >
                        Este vale no tiene productos.
                      </Table.Cell>
                    </Table.Row>
                  )}
                </Table.Body>
              </Table.Root>
            </Table.ScrollArea>
          </Box>
        </DialogBody>
      </DialogContent>
    </DialogRoot>
  );
};

export default ModalProductsTable;
